<template>
  <div class="chart-container">
    <div class="chart-header">
      <div class="chart-title">
        <h4>Compras por Mês</h4>
        <span class="chart-subtitle">Análise comparativa de quantidade e valor</span>
      </div>
      <div class="chart-legend">
        <div class="legend-item">
          <span class="legend-dot quantity"></span>
          <span>Quantidade</span>
        </div>
        <div class="legend-item">
          <span class="legend-dot value"></span>
          <span>Valor</span>
        </div>
      </div>
    </div>

    <div class="chart-wrapper">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <div class="chart-info">
      <div class="info-card">
        <span class="info-label">Total em Quantidade</span>
        <span class="info-value">{{ formatNumber(totalQuantity) }}</span>
        <span class="info-trend" :class="{ 'positive': quantityTrend >= 0 }">
          {{ formatTrend(quantityTrend) }}
          <font-awesome-icon :icon="['fas', quantityTrend >= 0 ? 'arrow-up' : 'arrow-down']" />
        </span>
      </div>

      <div class="info-card">
        <span class="info-label">Total em Valor</span>
        <span class="info-value">{{ formatCurrency(totalValue) }}</span>
        <span class="info-trend" :class="{ 'positive': valueTrend >= 0 }">
          {{ formatTrend(valueTrend) }}
          <font-awesome-icon :icon="['fas', valueTrend >= 0 ? 'arrow-up' : 'arrow-down']" />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
  emits: ['setLastMonthAmount', 'setLastMonthValue', 'setPenultimateMonthAmount', 'setPenultimateMonthValue'],
  data() {
    return {
      chart: null,
      totalQuantity: 0,
      totalValue: 0,
      quantityTrend: 0,
      valueTrend: 0
    }
  },
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '0';
      }
      return new Intl.NumberFormat('pt-BR').format(Number(value));
    },
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'R$ 0,00';
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(Number(value));
    },
    formatTrend(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '0.0%';
      }
      const numValue = Number(value);
      return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(1)}%`;
    },
    initChart(data) {
      const ctx = this.$refs.chartCanvas.getContext('2d');

      const gradientQuantity = ctx.createLinearGradient(0, 0, 0, 400);
      gradientQuantity.addColorStop(0, 'rgba(239, 68, 68, 0.2)');
      gradientQuantity.addColorStop(1, 'rgba(239, 68, 68, 0)');

      const gradientValue = ctx.createLinearGradient(0, 0, 0, 400);
      gradientValue.addColorStop(0, 'rgba(59, 130, 246, 0.2)');
      gradientValue.addColorStop(1, 'rgba(59, 130, 246, 0)');

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [
            {
              label: 'Quantidade',
              type: 'bar',
              data: data.quantities,
              borderColor: '#EF4444',
              backgroundColor: 'rgba(239, 68, 68, 0.6)',
              borderWidth: 1,
              yAxisID: 'y',
            },
            {
              label: 'Valor (R$)',
              type: 'line',
              data: data.values,
              borderColor: '#3B82F6',
              backgroundColor: gradientValue,
              tension: 0.4,
              fill: true,
              yAxisID: 'y1',
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              align: 'center',
              labels: {
                usePointStyle: true,
                boxWidth: 6,
                padding: 15
              }
            },
            tooltip: {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              titleColor: '#1E293B',
              bodyColor: '#1E293B',
              borderColor: '#E2E8F0',
              borderWidth: 1,
              padding: 12,
              boxPadding: 6,
              usePointStyle: true,
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label;
                  const value = context.raw;
                  return `${label}: ${label === 'Valor'
                      ? this.formatCurrency(value)
                      : this.formatNumber(value)
                    }`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: {
                display: false
              }
            },
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              grid: {
                color: '#E2E8F0'
              },
              ticks: {
                callback: (value) => this.formatNumber(value)
              },
              title: {
                display: true,
                text: 'Quantidade'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              grid: {
                drawOnChartArea: false,
              },
              ticks: {
                callback: (value) => this.formatCurrency(value)
              },
              title: {
                display: true,
                text: 'Valor (R$)'
              }
            }
          }
        }
      });
    },
    updateChart(data) {
      // Proteção para arrays vazios
      if (!data.quantities || !data.values || data.quantities.length === 0 || data.values.length === 0) {
        this.totalQuantity = 0;
        this.totalValue = 0;
        this.quantityTrend = 0;
        this.valueTrend = 0;
        return;
      }

      // Limpar valores inválidos e calcular totais
      const validQuantities = data.quantities.filter(val => val !== null && val !== undefined && !isNaN(val)).map(val => Number(val));
      const validValues = data.values.filter(val => val !== null && val !== undefined && !isNaN(val)).map(val => Number(val));

      this.totalQuantity = validQuantities.length > 0 ? validQuantities.reduce((a, b) => a + b, 0) : 0;
      this.totalValue = validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) : 0;
      
      const lastTwoQuantities = validQuantities.slice(-2);
      const lastTwoValues = validValues.slice(-2);
      
      this.quantityTrend = lastTwoQuantities.length >= 2 && lastTwoQuantities[0] !== 0 ? 
        ((lastTwoQuantities[1] - lastTwoQuantities[0]) / lastTwoQuantities[0]) * 100 : 0;
      this.valueTrend = lastTwoValues.length >= 2 && lastTwoValues[0] !== 0 ? 
        ((lastTwoValues[1] - lastTwoValues[0]) / lastTwoValues[0]) * 100 : 0;

      // Emitir valores para o componente pai (Dashboard)
      const lastMonthAmount = lastTwoQuantities.length >= 2 ? (lastTwoQuantities[1] || 0) : 0;
      const lastMonthValue = lastTwoValues.length >= 2 ? (lastTwoValues[1] || 0) : 0;
      const penultimateMonthAmount = lastTwoQuantities.length >= 2 ? (lastTwoQuantities[0] || 0) : 0;
      const penultimateMonthValue = lastTwoValues.length >= 2 ? (lastTwoValues[0] || 0) : 0;

      this.$emit('setLastMonthAmount', lastMonthAmount);
      this.$emit('setLastMonthValue', lastMonthValue);
      this.$emit('setPenultimateMonthAmount', penultimateMonthAmount);
      this.$emit('setPenultimateMonthValue', penultimateMonthValue);

      // Preparar dados limpos para o gráfico
      const cleanData = {
        labels: data.labels,
        quantities: validQuantities.length > 0 ? validQuantities : [0],
        values: validValues.length > 0 ? validValues : [0]
      };

      if (this.chart) {
        this.chart.data.labels = cleanData.labels;
        this.chart.data.datasets[0].data = cleanData.quantities;
        this.chart.data.datasets[1].data = cleanData.values;
        this.chart.update();
      } else {
        this.initChart(cleanData);
      }
    },
    async fetchData() {
      try {
        const apiBase = this.$store.getters.api + 'contracts/';
        
        // Buscar dados de quantidade e valor em paralelo
        const [quantityResponse, valueResponse] = await Promise.all([
          axios.get(apiBase + 'chart'),
          axios.get(apiBase + 'chartValue')
        ]);
        
        if (quantityResponse.data && quantityResponse.data.status === '00' &&
            valueResponse.data && valueResponse.data.status === '00') {
          
          const quantityData = quantityResponse.data.data;
          const valueData = valueResponse.data.data;
          
          const data = {
            labels: quantityData.labels || valueData.labels || [],
            quantities: quantityData.data || [],
            values: valueData.data || []
          };
          this.updateChart(data);
        } else {
          this.loadMockData();
        }
      } catch (error) {
        console.error('Erro ao buscar dados do gráfico:', error);
        // Fallback para dados simulados em caso de erro
        this.loadMockData();
      }
    },
    loadMockData() {
      const data = {
        labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
        quantities: [120, 150, 180, 220, 200, 250],
        values: [50000, 75000, 90000, 120000, 100000, 150000]
      };
      this.updateChart(data);
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>

<style scoped>
.chart-container {
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.chart-title {
  h4 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1E293B;
    margin: 0;
  }
}

.chart-subtitle {
  font-size: 0.875rem;
  color: #64748B;
}

.chart-legend {
  display: flex;
  gap: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748B;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-dot.quantity {
  background-color: #EF4444;
}

.legend-dot.value {
  background-color: #3B82F6;
}

.chart-wrapper {
  height: 300px;
  margin-bottom: 1.5rem;
}

.chart-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-card {
  background: #F8FAFC;
  border-radius: 0.75rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 0.875rem;
  color: #64748B;
  margin-bottom: 0.5rem;
}

.info-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1E293B;
  margin-bottom: 0.25rem;
}

.info-trend {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #DC2626;

  &.positive {
    color: #16A34A;
  }
}

@media (max-width: 768px) {
  .chart-container {
    padding: 1rem;
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .chart-wrapper {
    height: 250px;
  }
}
</style>
